/**
 * Plantillas de WhatsApp.
 *
 * Las plantillas se crean y se aprueban en Meta; la plataforma guarda una copia
 * local para poder ofrecerlas en el panel. Esta pantalla permite ver esa copia,
 * sincronizarla con Meta, crear plantillas nuevas y borrarlas.
 *
 * Fuera de la ventana de 24 horas, una plantilla aprobada es la **unica** forma
 * de escribirle a alguien, asi que aqui se deja claro el estado de cada una.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type {
  CreateTemplateRequest,
  TemplateView,
  WhatsappAccountView,
} from '@shared/api-types.ts';
import { ApiError, api, errorMessage, isAbortError } from '../api.ts';
import { formatDateTime } from '../format.ts';
import type { NoticeKind } from '../hooks.ts';
import type {
  TemplateButtonType,
  TemplateCategory,
  TemplateHeaderFormat,
  TemplateStatus,
} from '../template-utils.ts';
import {
  TEMPLATE_BUTTON_TEXT_MAX,
  TEMPLATE_BUTTON_TYPE_LABELS,
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_HEADER_FORMAT_LABELS,
  TEMPLATE_MAX_BUTTONS,
  TEMPLATE_STATUSES,
  TEMPLATE_STATUS_LABELS,
  TEMPLATE_STATUS_TONES,
  extractVariables,
  qualityLabel,
  qualityTone,
} from '../template-utils.ts';
import { Chip, EmptyState, ErrorBanner, Field, SkeletonRows, Spinner } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type TemplatesState =
  | { status: 'loading' }
  | { status: 'ready'; templates: TemplateView[] }
  | { status: 'error'; message: string };

type StatusFilter = TemplateStatus | 'all';

/** Formatos de cabecera que se pueden crear desde el panel. */
const HEADER_FORMATS: readonly TemplateHeaderFormat[] = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];
const BUTTON_TYPES: readonly TemplateButtonType[] = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];

const CATEGORY_HINTS: Record<TemplateCategory, string> = {
  UTILITY: 'Avisos sobre algo que el cliente ya ha pedido: estado de un pedido, una cita o un pago.',
  MARKETING: 'Promociones, novedades y ofertas. Requiere el consentimiento del cliente.',
  AUTHENTICATION: 'Codigos de un solo uso para verificar la identidad del cliente.',
};

// --- Formulario de alta ------------------------------------------------------

type FormButton = {
  id: number;
  type: TemplateButtonType;
  text: string;
  /** Direccion o numero, segun el tipo de boton. */
  target: string;
};

type TemplateFormValues = {
  accountId: string;
  name: string;
  language: string;
  category: TemplateCategory;
  headerEnabled: boolean;
  headerFormat: TemplateHeaderFormat;
  headerText: string;
  body: string;
  footer: string;
  buttons: FormButton[];
  /** Valores de ejemplo por posicion de `{{n}}`, que Meta exige para revisar. */
  headerExamples: Record<number, string>;
  bodyExamples: Record<number, string>;
};

const EMPTY_TEMPLATE_FORM: TemplateFormValues = {
  accountId: '',
  name: '',
  language: 'es',
  category: 'UTILITY',
  headerEnabled: false,
  headerFormat: 'TEXT',
  headerText: '',
  body: '',
  footer: '',
  buttons: [],
  headerExamples: {},
  bodyExamples: {},
};

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateTemplateForm(values: TemplateFormValues): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!values.accountId) errors.accountId = 'Elige el numero al que pertenece la plantilla.';

  const name = values.name.trim();
  if (!name) errors.name = 'Indica un nombre para la plantilla.';
  else if (!/^[a-z0-9_\s-]+$/i.test(name)) {
    errors.name = 'Solo puede llevar letras, numeros, espacios y guiones bajos.';
  }

  if (!values.language.trim()) errors.language = 'Indica el codigo de idioma (por ejemplo es).';

  const body = values.body.trim();
  if (!body) errors.body = 'La plantilla necesita un cuerpo con texto.';

  if (values.headerEnabled && values.headerFormat === 'TEXT') {
    if (!values.headerText.trim()) errors.header = 'Una cabecera de texto necesita su texto.';
    for (const position of extractVariables(values.headerText)) {
      if (!values.headerExamples[position]?.trim()) {
        errors.headerExample = 'Rellena los valores de ejemplo de la cabecera.';
      }
    }
  }

  for (const position of extractVariables(body)) {
    if (!values.bodyExamples[position]?.trim()) {
      errors.example =
        'Rellena los valores de ejemplo del cuerpo: Meta los exige para revisar la plantilla.';
    }
  }

  values.buttons.forEach((button, index) => {
    const key = `button_${index}`;
    const text = button.text.trim();
    const target = button.target.trim();

    if (!text) {
      errors[key] = 'Cada boton necesita su texto.';
      return;
    }
    if (text.length > TEMPLATE_BUTTON_TEXT_MAX) {
      errors[key] = `El texto del boton no puede pasar de ${TEMPLATE_BUTTON_TEXT_MAX} caracteres.`;
      return;
    }
    if (button.type === 'URL') {
      if (!target) errors[key] = 'Un boton de URL necesita la direccion.';
      else if (!isHttpUrl(target)) errors[key] = 'La direccion debe empezar por http:// o https://.';
    } else if (button.type === 'PHONE_NUMBER' && !target) {
      errors[key] = 'Un boton de telefono necesita el numero.';
    }
  });

  return errors;
}

/** Componentes tal y como los espera Meta al **crear** la plantilla. */
function toCreateTemplateRequest(values: TemplateFormValues): CreateTemplateRequest {
  const components: unknown[] = [];

  if (values.headerEnabled) {
    const header: Record<string, unknown> = { type: 'HEADER', format: values.headerFormat };
    if (values.headerFormat === 'TEXT') {
      const text = values.headerText.trim();
      header.text = text;
      const variables = extractVariables(text);
      if (variables.length > 0) {
        header.example = {
          header_text: variables.map((position) => values.headerExamples[position]?.trim() ?? ''),
        };
      }
    }
    components.push(header);
  }

  const bodyText = values.body.trim();
  const bodyVariables = extractVariables(bodyText);
  const body: Record<string, unknown> = { type: 'BODY', text: bodyText };
  if (bodyVariables.length > 0) {
    body.example = {
      body_text: [bodyVariables.map((position) => values.bodyExamples[position]?.trim() ?? '')],
    };
  }
  components.push(body);

  const footer = values.footer.trim();
  if (footer) components.push({ type: 'FOOTER', text: footer });

  if (values.buttons.length > 0) {
    components.push({
      type: 'BUTTONS',
      buttons: values.buttons.map((button) => {
        const built: Record<string, unknown> = { type: button.type, text: button.text.trim() };
        if (button.type === 'URL') built.url = button.target.trim();
        if (button.type === 'PHONE_NUMBER') built.phone_number = button.target.trim();
        return built;
      }),
    });
  }

  return {
    accountId: values.accountId,
    name: values.name.trim(),
    language: values.language.trim() || 'es',
    category: values.category,
    components,
  };
}

/**
 * Traduce el `details` de la API a errores por campo. `invalid_template` usa
 * claves como `body`, `example` o `button_0`; `invalid_body` usa las de zod
 * (`accountId`, `name`, `components...`). Lo que no encaja va al mensaje general.
 */
function mapTemplateServerErrors(details: Record<string, string[]>): {
  fields: Record<string, string>;
  general: string | null;
} {
  const fields: Record<string, string> = {};
  const general: string[] = [];

  for (const [key, messages] of Object.entries(details)) {
    const message = messages.join(' ');
    const flatButton = /^button_(\d+)$/.exec(key);
    if (flatButton) {
      fields[`button_${flatButton[1]}`] = message;
      continue;
    }
    const nestedButton = /buttons\.(\d+)/.exec(key);
    if (nestedButton) {
      fields[`button_${nestedButton[1]}`] = message;
      continue;
    }

    switch (key) {
      case 'body':
        fields.body = message;
        break;
      case 'example':
        fields.example = message;
        break;
      case 'accountId':
        fields.accountId = message;
        break;
      case 'name':
        fields.name = message;
        break;
      case 'language':
        fields.language = message;
        break;
      case 'category':
        fields.category = message;
        break;
      default:
        general.push(message);
        break;
    }
  }

  return { fields, general: general.length > 0 ? general.join(' ') : null };
}

/**
 * Alta de una plantilla. Explica que Meta la revisa antes de aprobarla, recoge
 * los valores de ejemplo que exige para las variables y construye los
 * componentes en el orden que espera la API.
 */
function CreateTemplateForm({
  accounts,
  accountsError,
  onCreated,
  onCancel,
}: {
  accounts: WhatsappAccountView[];
  accountsError: string | null;
  onCreated: (template: TemplateView) => void;
  onCancel: () => void;
}): ReactElement {
  const [values, setValues] = useState<TemplateFormValues>(EMPTY_TEMPLATE_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const nextButtonId = useRef(1);

  const update = useCallback(
    <K extends keyof TemplateFormValues>(key: K, value: TemplateFormValues[K]): void => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearErrors = useCallback((...keys: string[]): void => {
    setFieldErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of keys) {
        if (next[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  // Primer numero disponible preseleccionado: casi siempre solo hay uno.
  useEffect(() => {
    if (values.accountId) return;
    const first = accounts[0];
    if (!first) return;
    setValues((prev) => (prev.accountId ? prev : { ...prev, accountId: first.id }));
  }, [accounts, values.accountId]);

  const bodyVariables = useMemo(() => extractVariables(values.body), [values.body]);
  const headerVariables = useMemo(
    () =>
      values.headerEnabled && values.headerFormat === 'TEXT'
        ? extractVariables(values.headerText)
        : [],
    [values.headerEnabled, values.headerFormat, values.headerText],
  );

  const addButton = useCallback((): void => {
    setValues((prev) => {
      if (prev.buttons.length >= TEMPLATE_MAX_BUTTONS) return prev;
      const id = nextButtonId.current;
      nextButtonId.current += 1;
      return {
        ...prev,
        buttons: [...prev.buttons, { id, type: 'QUICK_REPLY', text: '', target: '' }],
      };
    });
  }, []);

  const updateButton = useCallback(
    (index: number, patch: Partial<Omit<FormButton, 'id'>>): void => {
      setValues((prev) => ({
        ...prev,
        buttons: prev.buttons.map((button, position) =>
          position === index ? { ...button, ...patch } : button,
        ),
      }));
      clearErrors(`button_${index}`);
    },
    [clearErrors],
  );

  const removeButton = useCallback(
    (index: number): void => {
      setValues((prev) => ({
        ...prev,
        buttons: prev.buttons.filter((_, position) => position !== index),
      }));
      // Los indices siguientes se desplazan: se limpian todos los avisos de boton.
      clearErrors(...Array.from({ length: TEMPLATE_MAX_BUTTONS }, (_, i) => `button_${i}`));
    },
    [clearErrors],
  );

  const setExample = useCallback(
    (scope: 'body' | 'header', position: number, value: string): void => {
      setValues((prev) => ({
        ...prev,
        bodyExamples:
          scope === 'body' ? { ...prev.bodyExamples, [position]: value } : prev.bodyExamples,
        headerExamples:
          scope === 'header' ? { ...prev.headerExamples, [position]: value } : prev.headerExamples,
      }));
      clearErrors(scope === 'body' ? 'example' : 'headerExample');
    },
    [clearErrors],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const errors = validateTemplateForm(values);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setFormError('Revisa los campos marcados.');
        return;
      }

      setFieldErrors({});
      setFormError(null);
      setSubmitting(true);

      api
        .createTemplate(toCreateTemplateRequest(values))
        .then((created) => {
          setSubmitting(false);
          onCreated(created);
        })
        .catch((error: unknown) => {
          setSubmitting(false);
          if (error instanceof ApiError && error.status === 400 && error.invalidFields) {
            const parsed = mapTemplateServerErrors(error.invalidFields);
            setFieldErrors(parsed.fields);
            setFormError(parsed.general ?? error.message);
            return;
          }
          setFormError(errorMessage(error));
        });
    },
    [onCreated, values],
  );

  const hasExamples = bodyVariables.length > 0 || headerVariables.length > 0;

  return (
    <section className="card">
      <h2 className="card__title">Nueva plantilla</h2>
      <p className="card__hint">
        Se crea en Meta desde el numero elegido. Meta la revisa antes de aprobarla, de modo que
        quedara «en revision» y no se podra usar todavia para escribir a los clientes.
      </p>

      <div className="banner banner--warning" role="alert">
        <Icon name="warning" className="banner__icon" />
        <p className="banner__text">
          Meta revisa cada plantilla nueva: mientras no la apruebe no se puede enviar, ni dentro ni
          fuera de la ventana de 24 h. Suele tardar unos minutos, aunque a veces tarda bastante mas.
        </p>
      </div>

      {accountsError ? <ErrorBanner message={accountsError} /> : null}

      {!accountsError && accounts.length === 0 ? (
        <div className="banner banner--warning" role="status">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Todavia no hay ningun numero de WhatsApp dado de alta, asi que no se puede crear una
            plantilla. Anade primero un numero en la seccion «Cuentas».
          </p>
        </div>
      ) : null}

      <fieldset className="fieldset" disabled={submitting}>
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid--2">
            <Field
              label="Numero"
              htmlFor="tpl-account"
              required
              hint="Numero de WhatsApp desde el que se enviara la plantilla."
            >
              <select
                id="tpl-account"
                className="select"
                value={values.accountId}
                onChange={(event) => {
                  update('accountId', event.target.value);
                  clearErrors('accountId');
                }}
              >
                <option value="">Elige un numero</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                    {account.displayPhoneNumber ? ` · ${account.displayPhoneNumber}` : ''}
                  </option>
                ))}
              </select>
              {fieldErrors.accountId ? <p className="field__error">{fieldErrors.accountId}</p> : null}
            </Field>

            <Field
              label="Nombre"
              htmlFor="tpl-name"
              required
              hint="En minusculas, numeros y guiones bajos; por ejemplo aviso_pedido_listo. No se puede repetir en el mismo idioma."
            >
              <input
                id="tpl-name"
                className="input input--code"
                maxLength={512}
                value={values.name}
                placeholder="aviso_pedido_listo"
                onChange={(event) => {
                  update('name', event.target.value);
                  clearErrors('name');
                }}
              />
              {fieldErrors.name ? <p className="field__error">{fieldErrors.name}</p> : null}
            </Field>

            <Field
              label="Idioma"
              htmlFor="tpl-language"
              required
              hint="Codigo de idioma de la plantilla, por ejemplo es o es_ES."
            >
              <input
                id="tpl-language"
                className="input input--code"
                maxLength={10}
                value={values.language}
                placeholder="es"
                onChange={(event) => {
                  update('language', event.target.value);
                  clearErrors('language');
                }}
              />
              {fieldErrors.language ? <p className="field__error">{fieldErrors.language}</p> : null}
            </Field>

            <Field
              label="Categoria"
              htmlFor="tpl-category"
              required
              hint={CATEGORY_HINTS[values.category]}
            >
              <select
                id="tpl-category"
                className="select"
                value={values.category}
                onChange={(event) => {
                  update('category', event.target.value as TemplateCategory);
                  clearErrors('category');
                }}
              >
                {TEMPLATE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {TEMPLATE_CATEGORY_LABELS[category]}
                  </option>
                ))}
              </select>
              {fieldErrors.category ? <p className="field__error">{fieldErrors.category}</p> : null}
            </Field>
          </div>

          <h3 className="card__title">Cabecera (opcional)</h3>
          <label className="checkline" htmlFor="tpl-header-enabled">
            <input
              id="tpl-header-enabled"
              type="checkbox"
              checked={values.headerEnabled}
              onChange={(event) => {
                update('headerEnabled', event.target.checked);
                clearErrors('header', 'headerExample');
              }}
            />
            <span>Anadir una cabecera al mensaje</span>
          </label>

          {values.headerEnabled ? (
            <div className="grid grid--2">
              <Field label="Tipo de cabecera" htmlFor="tpl-header-format">
                <select
                  id="tpl-header-format"
                  className="select"
                  value={values.headerFormat}
                  onChange={(event) => {
                    update('headerFormat', event.target.value as TemplateHeaderFormat);
                    clearErrors('header', 'headerExample');
                  }}
                >
                  {HEADER_FORMATS.map((format) => (
                    <option key={format} value={format}>
                      {TEMPLATE_HEADER_FORMAT_LABELS[format]}
                    </option>
                  ))}
                </select>
              </Field>

              {values.headerFormat === 'TEXT' ? (
                <Field
                  label="Texto de la cabecera"
                  htmlFor="tpl-header-text"
                  required
                  hint="Maximo 60 caracteres. Admite variables {{1}}, {{2}}."
                >
                  <input
                    id="tpl-header-text"
                    className="input"
                    maxLength={60}
                    value={values.headerText}
                    placeholder="Pedido {{1}}"
                    onChange={(event) => {
                      update('headerText', event.target.value);
                      clearErrors('header', 'headerExample');
                    }}
                  />
                  {fieldErrors.header ? <p className="field__error">{fieldErrors.header}</p> : null}
                </Field>
              ) : (
                <p className="field__hint">
                  {`Meta pedira el archivo (${TEMPLATE_HEADER_FORMAT_LABELS[values.headerFormat].toLowerCase()}) al enviar el mensaje. Los ejemplos de cabecera solo se piden para las cabeceras de texto.`}
                </p>
              )}
            </div>
          ) : null}

          <h3 className="card__title">Cuerpo</h3>
          <Field
            label="Texto del mensaje"
            htmlFor="tpl-body"
            required
            hint="Obligatorio. Puedes usar variables {{1}}, {{2}}... para los datos que cambian en cada envio; al enviarlo, el agente rellena sus valores."
          >
            <textarea
              id="tpl-body"
              className="input input--tall"
              rows={5}
              maxLength={1024}
              value={values.body}
              placeholder={'Hola {{1}}, tu pedido {{2}} ya esta listo para recoger.'}
              onChange={(event) => {
                update('body', event.target.value);
                clearErrors('body', 'example');
              }}
            />
            {fieldErrors.body ? <p className="field__error">{fieldErrors.body}</p> : null}
          </Field>

          <Field
            label="Pie (opcional)"
            htmlFor="tpl-footer"
            hint="Una linea corta al final del mensaje, sin variables. Maximo 60 caracteres."
          >
            <input
              id="tpl-footer"
              className="input"
              maxLength={60}
              value={values.footer}
              placeholder="Gracias por confiar en nosotros"
              onChange={(event) => update('footer', event.target.value)}
            />
          </Field>

          {hasExamples ? (
            <>
              <h3 className="card__title">Valores de ejemplo</h3>
              <p className="card__hint">
                Meta revisa la plantilla con estos valores. No se envian a nadie: solo sirven para
                que la apruebe. Sin ellos, rechaza la plantilla por tener variables.
              </p>

              {headerVariables.length > 0 ? (
                <div className="grid grid--3">
                  {headerVariables.map((position) => (
                    <Field
                      key={`header-${position}`}
                      label={`Cabecera {{${position}}}`}
                      htmlFor={`tpl-header-example-${position}`}
                      required
                    >
                      <input
                        id={`tpl-header-example-${position}`}
                        className="input"
                        maxLength={60}
                        value={values.headerExamples[position] ?? ''}
                        placeholder="12345"
                        onChange={(event) => setExample('header', position, event.target.value)}
                      />
                    </Field>
                  ))}
                </div>
              ) : null}

              {bodyVariables.length > 0 ? (
                <div className="grid grid--3">
                  {bodyVariables.map((position) => (
                    <Field
                      key={`body-${position}`}
                      label={`Cuerpo {{${position}}}`}
                      htmlFor={`tpl-body-example-${position}`}
                      required
                    >
                      <input
                        id={`tpl-body-example-${position}`}
                        className="input"
                        maxLength={200}
                        value={values.bodyExamples[position] ?? ''}
                        placeholder="Maria"
                        onChange={(event) => setExample('body', position, event.target.value)}
                      />
                    </Field>
                  ))}
                </div>
              ) : null}

              {fieldErrors.example || fieldErrors.headerExample ? (
                <p className="field__error">{fieldErrors.example ?? fieldErrors.headerExample}</p>
              ) : null}
            </>
          ) : null}

          <h3 className="card__title">Botones (opcional)</h3>
          <p className="card__hint">
            Hasta {TEMPLATE_MAX_BUTTONS} botones. Una respuesta rapida devuelve una opcion, una URL
            abre una pagina y un telefono inicia una llamada.
          </p>

          {values.buttons.length > 0 ? (
            <div className="tpl-buttons">
              {values.buttons.map((button, index) => (
                <div className="tpl-button" key={button.id}>
                  <div className="grid grid--3">
                    <Field label="Tipo" htmlFor={`tpl-button-type-${index}`}>
                      <select
                        id={`tpl-button-type-${index}`}
                        className="select"
                        value={button.type}
                        onChange={(event) =>
                          updateButton(index, {
                            type: event.target.value as TemplateButtonType,
                            target: '',
                          })
                        }
                      >
                        {BUTTON_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {TEMPLATE_BUTTON_TYPE_LABELS[type]}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field
                      label="Texto"
                      htmlFor={`tpl-button-text-${index}`}
                      required
                      hint={`Maximo ${TEMPLATE_BUTTON_TEXT_MAX} caracteres.`}
                    >
                      <input
                        id={`tpl-button-text-${index}`}
                        className="input"
                        maxLength={TEMPLATE_BUTTON_TEXT_MAX}
                        value={button.text}
                        placeholder="Ver pedido"
                        onChange={(event) => updateButton(index, { text: event.target.value })}
                      />
                    </Field>

                    {button.type === 'QUICK_REPLY' ? (
                      <p className="field__hint">
                        La respuesta rapida no necesita destino: el cliente la pulsa y WhatsApp
                        devuelve ese texto al panel.
                      </p>
                    ) : (
                      <Field
                        label={button.type === 'URL' ? 'Direccion' : 'Numero'}
                        htmlFor={`tpl-button-target-${index}`}
                        required
                        hint={
                          button.type === 'URL'
                            ? 'Con http:// o https://. Puede cerrar con una variable.'
                            : 'Con prefijo internacional, por ejemplo +34600000000.'
                        }
                      >
                        <input
                          id={`tpl-button-target-${index}`}
                          className="input input--code"
                          maxLength={button.type === 'URL' ? 2000 : 20}
                          value={button.target}
                          placeholder={
                            button.type === 'URL' ? 'https://mi-tienda.com/pedido' : '+34600000000'
                          }
                          onChange={(event) => updateButton(index, { target: event.target.value })}
                        />
                      </Field>
                    )}
                  </div>

                  {fieldErrors[`button_${index}`] ? (
                    <p className="field__error">{fieldErrors[`button_${index}`]}</p>
                  ) : null}

                  <div className="page__actions page__actions--end">
                    <button
                      type="button"
                      className="button button--ghost button--sm"
                      onClick={() => removeButton(index)}
                    >
                      <Icon name="trash" />
                      Quitar boton
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="page__actions page__actions--end">
            <button
              type="button"
              className="button button--ghost"
              onClick={addButton}
              disabled={values.buttons.length >= TEMPLATE_MAX_BUTTONS}
              title={
                values.buttons.length >= TEMPLATE_MAX_BUTTONS
                  ? `Como maximo ${TEMPLATE_MAX_BUTTONS} botones`
                  : 'Anadir otro boton'
              }
            >
              <Icon name="plus" />
              Anadir boton
            </button>
          </div>

          {formError ? (
            <div className="banner banner--error" role="alert">
              <Icon name="error" className="banner__icon" />
              <p className="banner__text">{formError}</p>
            </div>
          ) : null}

          <div className="page__actions page__actions--end">
            <button
              type="button"
              className="button button--ghost"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={submitting || accounts.length === 0}
              title={
                accounts.length === 0 ? 'Primero da de alta un numero de WhatsApp' : undefined
              }
            >
              {submitting ? (
                <Spinner label="Enviando a Meta…" />
              ) : (
                <>
                  <Icon name="plus" />
                  Crear la plantilla
                </>
              )}
            </button>
          </div>
        </form>
      </fieldset>
    </section>
  );
}

// --- Pantalla ----------------------------------------------------------------

export function TemplatesScreen({
  onNotice,
  canWrite,
}: {
  onNotice: (kind: NoticeKind, text: string) => void;
  /** Crear y borrar plantillas es configuracion: solo propietarios y administradores. */
  canWrite: boolean;
}): ReactElement {
  const [state, setState] = useState<TemplatesState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [accounts, setAccounts] = useState<WhatsappAccountView[]>([]);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .listTemplates({}, controller.signal)
      .then((templates) => {
        if (!controller.signal.aborted) setState({ status: 'ready', templates });
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [reloadToken]);

  // La lista de numeros alimenta el filtro y el formulario de alta.
  useEffect(() => {
    const controller = new AbortController();
    setAccountsError(null);
    api
      .listAccounts(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setAccounts(list);
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setAccountsError(errorMessage(error));
      });
    return () => controller.abort();
  }, [reloadToken]);

  const templates = state.status === 'ready' ? state.templates : [];

  /** Numeros del filtro: los dados de alta mas los que aparezcan en plantillas. */
  const accountOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const account of accounts) map.set(account.id, account.name);
    for (const template of templates) {
      if (!map.has(template.accountId)) {
        map.set(template.accountId, template.accountName ?? 'Numero sin nombre');
      }
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [accounts, templates]);

  const visible = useMemo(
    () =>
      templates.filter(
        (template) =>
          (statusFilter === 'all' || template.status === statusFilter) &&
          (accountFilter === 'all' || template.accountId === accountFilter),
      ),
    [accountFilter, statusFilter, templates],
  );

  const pendingTemplate = templates.find((template) => template.id === pendingDelete) ?? null;
  const hasFilters = statusFilter !== 'all' || accountFilter !== 'all';

  const handleSync = useCallback((): void => {
    setSyncing(true);
    setSyncError(null);
    api
      .syncTemplates(accountFilter === 'all' ? {} : { accountId: accountFilter })
      .then((response) => {
        const total = response.templates;
        onNotice(
          'success',
          total === 0
            ? 'Meta no devolvio ninguna plantilla. La lista ya esta actualizada.'
            : `Meta devolvio ${total} ${total === 1 ? 'plantilla' : 'plantillas'}. La lista ya esta actualizada.`,
        );
        setReloadToken((token) => token + 1);
      })
      .catch((error: unknown) => setSyncError(errorMessage(error)))
      .finally(() => setSyncing(false));
  }, [accountFilter, onNotice]);

  const handleCreated = useCallback(
    (template: TemplateView): void => {
      setCreateOpen(false);
      setState((prev) =>
        prev.status === 'ready'
          ? {
              status: 'ready',
              templates: [template, ...prev.templates.filter((item) => item.id !== template.id)],
            }
          : { status: 'ready', templates: [template] },
      );
      onNotice(
        'success',
        `Plantilla «${template.name}» enviada a Meta. Queda en revision hasta que la apruebe.`,
      );
    },
    [onNotice],
  );

  const handleDelete = useCallback(
    (template: TemplateView): void => {
      setDeleting(template.id);
      api
        .deleteTemplate(template.id)
        .then(() => {
          // La API no la elimina: la marca como borrada para no perder el historico.
          setState((prev) =>
            prev.status === 'ready'
              ? {
                  status: 'ready',
                  templates: prev.templates.map((item) =>
                    item.id === template.id ? { ...item, status: 'DELETED' } : item,
                  ),
                }
              : prev,
          );
          setPendingDelete(null);
          onNotice('success', `Plantilla «${template.name}» borrada en Meta y en el panel.`);
        })
        .catch((error: unknown) => onNotice('error', errorMessage(error)))
        .finally(() => setDeleting(null));
    },
    [onNotice],
  );

  const clearFilters = useCallback((): void => {
    setStatusFilter('all');
    setAccountFilter('all');
  }, []);

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Plantillas de WhatsApp</h1>
          <p className="page__subtitle">
            Copia local de las plantillas de la organizacion. Se crean y se aprueban en Meta; fuera
            de la ventana de 24 h, una plantilla aprobada es la unica forma de escribir a alguien.
          </p>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={() => setCreateOpen(true)}
            disabled={!canWrite || createOpen}
            title={
              canWrite
                ? undefined
                : 'Solo el propietario y los administradores pueden crear plantillas'
            }
          >
            <Icon name="plus" />
            Nueva plantilla
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={handleSync}
            disabled={syncing}
            title="Trae de Meta las plantillas y su estado actual"
          >
            {syncing ? <Spinner label="Sincronizando…" /> : (<><Icon name="refresh" />Sincronizar con Meta</>)}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            <Icon name="refresh" />
            Actualizar
          </button>
        </div>
      </header>

      {!canWrite ? (
        <div className="banner banner--warning" role="status">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Solo el propietario y los administradores pueden crear o borrar plantillas. Puedes
            consultarlas y sincronizarlas con Meta, pero no cambiarlas.
          </p>
        </div>
      ) : null}

      {syncError ? (
        <ErrorBanner
          message={syncError}
          onRetry={handleSync}
          retryLabel="Reintentar la sincronizacion"
        />
      ) : null}

      {createOpen ? (
        <CreateTemplateForm
          accounts={accounts}
          accountsError={accountsError}
          onCreated={handleCreated}
          onCancel={() => setCreateOpen(false)}
        />
      ) : null}

      <section className="card">
        <div className="filters">
          <div className="filters__row">
            <label className="filters__field">
              <span className="filters__label">Estado</span>
              <select
                className="select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              >
                <option value="all">Todos</option>
                {TEMPLATE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {TEMPLATE_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </label>

            <label className="filters__field">
              <span className="filters__label">Numero</span>
              <select
                className="select"
                value={accountFilter}
                onChange={(event) => setAccountFilter(event.target.value)}
                disabled={accountOptions.length === 0}
              >
                <option value="all">Todos</option>
                {accountOptions.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {hasFilters ? (
            <div className="filters__row filters__row--end">
              <button type="button" className="button button--ghost button--sm" onClick={clearFilters}>
                <Icon name="close" />
                Quitar los filtros
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {pendingTemplate ? (
        <div className="banner banner--warning" role="alert">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Vas a borrar «{pendingTemplate.name}»: se elimina tambien en Meta y dejara de poder
            usarse para escribir a los clientes. Los mensajes ya enviados con ella no se tocan.
          </p>
          <button
            type="button"
            className="button button--ghost button--sm"
            onClick={() => setPendingDelete(null)}
            disabled={deleting === pendingTemplate.id}
          >
            Cancelar
          </button>
        </div>
      ) : null}

      {state.status === 'loading' ? <SkeletonRows rows={4} /> : null}

      {state.status === 'error' ? (
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      ) : null}

      {state.status === 'ready' && templates.length === 0 ? (
        <EmptyState
          icon="templates"
          title="Todavia no hay plantillas"
          description="Las plantillas se crean aqui y las aprueba Meta. Si ya tienes plantillas en Meta para estos numeros, sincroniza para traerlas."
          action={
            <div className="page__actions">
              <button
                type="button"
                className="button button--primary"
                onClick={() => setCreateOpen(true)}
                disabled={!canWrite || createOpen}
                title={
                  canWrite
                    ? undefined
                    : 'Solo el propietario y los administradores pueden crear plantillas'
                }
              >
                <Icon name="plus" />
                Crear la primera plantilla
              </button>
              <button
                type="button"
                className="button button--ghost"
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? <Spinner label="Sincronizando…" /> : (<><Icon name="refresh" />Sincronizar con Meta</>)}
              </button>
            </div>
          }
        />
      ) : null}

      {state.status === 'ready' && templates.length > 0 && visible.length === 0 ? (
        <EmptyState
          icon="search"
          title="Ninguna plantilla con estos filtros"
          description="Prueba con otro estado o con otro numero."
          action={
            <button type="button" className="button button--ghost" onClick={clearFilters}>
              <Icon name="close" />
              Quitar los filtros
            </button>
          }
        />
      ) : null}

      {visible.length > 0 ? (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Idioma</th>
                <th>Categoria</th>
                <th>Estado</th>
                <th>Calidad</th>
                <th>Numero</th>
                <th>Sincronizada</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((template) => (
                <tr key={template.id}>
                  <td className="table__strong">
                    {template.name}
                    {template.rejectedReason ? (
                      <p className="field__hint">Motivo: {template.rejectedReason}</p>
                    ) : null}
                  </td>
                  <td className="table__mono">{template.language}</td>
                  <td>{TEMPLATE_CATEGORY_LABELS[template.category]}</td>
                  <td>
                    <Chip
                      tone={TEMPLATE_STATUS_TONES[template.status]}
                      title={template.rejectedReason ?? undefined}
                    >
                      {TEMPLATE_STATUS_LABELS[template.status]}
                    </Chip>
                  </td>
                  <td>
                    <Chip tone={qualityTone(template.qualityRating)}>
                      {qualityLabel(template.qualityRating)}
                    </Chip>
                  </td>
                  <td>{template.accountName ?? 'Numero sin nombre'}</td>
                  <td>{template.syncedAt ? formatDateTime(template.syncedAt) : 'Nunca'}</td>
                  <td>
                    <div className="rowactions">
                      {pendingDelete === template.id ? (
                        <>
                          <button
                            type="button"
                            className="button button--danger button--sm"
                            onClick={() => handleDelete(template)}
                            disabled={deleting === template.id}
                          >
                            {deleting === template.id ? (
                              <Spinner label="Borrando…" />
                            ) : (
                              'Confirmar borrado'
                            )}
                          </button>
                          <button
                            type="button"
                            className="button button--ghost button--sm"
                            onClick={() => setPendingDelete(null)}
                            disabled={deleting === template.id}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="button button--ghost button--sm"
                          onClick={() => setPendingDelete(template.id)}
                          disabled={!canWrite}
                          title={
                            canWrite
                              ? 'Borra la plantilla en Meta y en el panel'
                              : 'Solo el propietario y los administradores pueden borrar plantillas'
                          }
                        >
                          <Icon name="trash" />
                          Borrar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
